import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'redux';

import AlertsIcon from 'mdi-react/BellOutlineIcon';
import AlertsIconActive from 'mdi-react/BellIcon';
import ListsIcon from 'mdi-react/HeartOutlineIcon';
import ListsIconActive from 'mdi-react/HeartIcon';
import AnalyticsIcon from 'mdi-react/PollBoxIcon';
import FilterIcon from 'mdi-react/FilterVariantIcon';

import HideableBox from '@prisjakt/ui-hideable-box';
import ExperimentEditor from '@prisjakt/ui-experiment/src/editor';
import { layout } from '@prisjakt/ui-base';
import UserAvatar from '@prisjakt/ui-user-avatar';

import listActions from '../../actions/lists';
import alertActions from '../../actions/alert';
import filterActions from '../../actions/filter';

import { useViewportContext } from '../../../../components/viewport/context';
import Menu from '../../../../components/menu-new';
import MenuItem from '../../../../components/menu-new/components/menu-item';
import AccountMenu from '../../../account/menu-new';

import List from '../../views/list';
import Lists from '../../views/lists';
import Alerts from '../../views/alerts';
import SidebarFilter from '../../views/filter';

import {
  SIDEBAR_STATE,
  SIDEBAR_LOCALSTORAGE_KEY,
  DEFAULT_SIDEBAR_STATE,
} from '../../constants';

import { AppState } from '../../../../reducers';

import * as S from './styles';

import { User } from '../../../../types';

type OwnProps = {
  history: {
    push: (path: string) => void;
  };
  className?: string;
  hasOldMenu: boolean;
};

type StateProps = {
  me: User;
  isUserAuthenticated: boolean;
  open: boolean;
  tab:
    | SIDEBAR_STATE.NONE
    | SIDEBAR_STATE.LISTS
    | SIDEBAR_STATE.ALERTS
    | SIDEBAR_STATE.FILTER;
  previousTab:
    | SIDEBAR_STATE.LISTS
    | SIDEBAR_STATE.ALERTS
    | SIDEBAR_STATE.FILTER;
  selectedList?: number;
  showFilter: boolean;
  showHorizontalFilter: boolean;
};

type DispatchProps = {
  closeLists: () => void;
  openLists: () => void;
  openAlerts: () => void;
  toggleFilter: () => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const Sidebar = ({
  isUserAuthenticated = false,
  className,
  tab = SIDEBAR_STATE.NONE,
  previousTab = SIDEBAR_STATE.LISTS,
  open = false,
  selectedList,
  closeLists = () => {},
  openLists = () => {},
  openAlerts = () => {},
  toggleFilter = () => {},
  me,
  history,
  hasOldMenu,
  showFilter,
  showHorizontalFilter,
}: Props) => {
  const { isDesktop } = useViewportContext();

  const closeSidebar = useCallback(() => {
    closeLists();
  }, [closeLists]);

  const toggleView = useCallback(
    state => {
      if (open && state === tab) {
        closeSidebar();
        return;
      }

      switch (state) {
        case SIDEBAR_STATE.LISTS:
          openLists();
          break;
        case SIDEBAR_STATE.ALERTS:
          openAlerts();
          break;
        case SIDEBAR_STATE.FILTER:
          toggleFilter();
          break;
        default:
          closeSidebar();
      }
    },
    [closeSidebar, open, openAlerts, openLists, tab, toggleFilter]
  );

  const handleToggleButton = useCallback(() => {
    if (open) {
      toggleView(SIDEBAR_STATE.NONE);
      return;
    }

    // Open default view if previous view was filter but no longer
    // on a leaf category page, or if filter is set to HorizontalFilter,
    // or `previousTab` is empty.
    if (
      !previousTab ||
      (previousTab === SIDEBAR_STATE.FILTER && !showFilter) ||
      (previousTab === SIDEBAR_STATE.FILTER && showHorizontalFilter)
    ) {
      toggleView(DEFAULT_SIDEBAR_STATE);
      return;
    }

    toggleView(previousTab);
  }, [open, previousTab, showFilter, showHorizontalFilter, toggleView]);

  // Open tab stored in localStorage on initial mount
  // Empty second argument `[]` to `useEffect` causes it to
  // only run on initial mount and unmount instead of on every update.
  useEffect(() => {
    if (tab !== SIDEBAR_STATE.NONE) {
      toggleView(tab);
    } else {
      try {
        const storage = localStorage.getItem(SIDEBAR_LOCALSTORAGE_KEY);
        const storedTab = storage && JSON.parse(storage).tab;

        toggleView(storedTab);
      } catch (e) {
        console.error(e);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeTab = open ? tab || SIDEBAR_STATE.NONE : SIDEBAR_STATE.NONE;
  const isListTabActive = activeTab === SIDEBAR_STATE.LISTS;
  const isAlertsTabActive = activeTab === SIDEBAR_STATE.ALERTS;
  const isFilterTabActive = activeTab === SIDEBAR_STATE.FILTER;
  const ANIMATION_SPEED = 'SLOW';

  const renderView = () => {
    let view;
    switch (tab) {
      case SIDEBAR_STATE.LISTS:
        view = (
          <S.HideableBox visible={tab === SIDEBAR_STATE.LISTS} speed="INSTANT">
            {selectedList ? (
              <List id={selectedList} draggable />
            ) : (
              <Lists draggable />
            )}
          </S.HideableBox>
        );
        break;
      case SIDEBAR_STATE.ALERTS:
        view = (
          <S.HideableBox visible={tab === SIDEBAR_STATE.ALERTS} speed="INSTANT">
            <Alerts />
          </S.HideableBox>
        );
        break;
      case SIDEBAR_STATE.FILTER:
        view = showFilter && (
          <S.HideableBox visible={tab === SIDEBAR_STATE.FILTER} speed="INSTANT">
            <SidebarFilter />
          </S.HideableBox>
        );
        break;
      default:
        return null;
    }

    return view && <S.SidebarContent>{view}</S.SidebarContent>;
  };

  return (
    <S.Sidebar
      tab={activeTab}
      className={className}
      animationSpeed={ANIMATION_SPEED}
      data-test="SidebarDesktop"
    >
      <S.Sticky hasOldMenu={hasOldMenu}>
        <S.SidebarMenu>
          <S.TopBar>
            <S.MenuButton
              active={isListTabActive}
              onClick={() => toggleView(SIDEBAR_STATE.LISTS)}
              data-test="MenuButtonLists"
            >
              {isListTabActive ? <ListsIconActive /> : <ListsIcon />}
            </S.MenuButton>
            <S.MenuButton
              active={isAlertsTabActive}
              onClick={() => toggleView(SIDEBAR_STATE.ALERTS)}
              data-test="MenuButtonAlerts"
            >
              {isAlertsTabActive ? <AlertsIconActive /> : <AlertsIcon />}
            </S.MenuButton>

            <S.MenuButton
              active={isFilterTabActive}
              onClick={() => toggleView(SIDEBAR_STATE.FILTER)}
              hidden={!showFilter || showHorizontalFilter}
              data-test="MenuButtonFilter"
            >
              <FilterIcon />
            </S.MenuButton>
          </S.TopBar>

          {isDesktop && (
            <S.BottomBar hasOldMenu={hasOldMenu}>
              {me && me.analyticsAccess && (
                <Menu
                  toggleElementStyles={{
                    width: layout.sidebarMenuWidth.desktop.rem,
                  }}
                  toggleElement={
                    <S.MenuButton active={false} hoverEffect="color">
                      <AnalyticsIcon />
                    </S.MenuButton>
                  }
                  position="right"
                  grow="up"
                  width="200px"
                >
                  {me.analyticsAccess.stores &&
                    me.analyticsAccess.stores.map(
                      store =>
                        store && (
                          <MenuItem
                            key={store.name}
                            href={store.url}
                            data-test="MenuItemStore"
                          >
                            {store.name}
                          </MenuItem>
                        )
                    )}
                  {me.analyticsAccess.brands &&
                    me.analyticsAccess.brands.map(
                      brand =>
                        brand && (
                          <MenuItem
                            key={brand.name}
                            href={brand.url}
                            data-test="MenuItemBrand"
                          >
                            {brand.name}
                          </MenuItem>
                        )
                    )}
                </Menu>
              )}

              <AccountMenu
                position="right"
                grow="up"
                toggleElement={
                  <S.MenuButton active={false} hoverEffect="color">
                    <UserAvatar {...me} size={24} />
                  </S.MenuButton>
                }
                toggleElementStyles={{
                  width: layout.sidebarMenuWidth.desktop.rem,
                }}
              />

              <ExperimentEditor isFixed={false} />

              <S.Divider />

              <S.MenuButton
                active={false}
                hoverEffect="background"
                onClick={handleToggleButton}
              >
                <S.ToggleSidebarIcon
                  size={32}
                  open={tab !== SIDEBAR_STATE.NONE}
                />
              </S.MenuButton>
            </S.BottomBar>
          )}
        </S.SidebarMenu>
        {isDesktop && (
          <HideableBox
            speed={ANIMATION_SPEED}
            slide="LEFT"
            visible={activeTab !== 'none'}
          >
            {renderView()}
          </HideableBox>
        )}
      </S.Sticky>
    </S.Sidebar>
  );
};

Sidebar.displayName = 'Sidebar';

const mapStateToProps = (state: AppState): StateProps => ({
  ...state.areas.sidebar,
  isUserAuthenticated: state.areas.account.isAuthenticated,
  me: state.areas.account.me,
  showFilter: state.areas.category.filter.showFilter,
  showHorizontalFilter: state.areas.category.filter.showHorizontalFilter,
});

const dispatchProps = {
  openLists: listActions.openLists,
  closeLists: listActions.closeLists,
  openAlerts: alertActions.openAlerts,
  closeAlerts: alertActions.closeAlerts,
  toggleFilter: filterActions.toggleFilter,
};

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    dispatchProps
  )
)(Sidebar);
